/**
 * PasswordRecoveryPages — Forgot Password + Reset Password (public).
 *
 * Exporte 2 composants minimalistes, design aligné avec Login.jsx :
 *   - ForgotPassword  → /forgot-password
 *   - ResetPassword   → /reset-password?token=xxx
 *
 * Backend : POST /api/auth/forgot-password, POST /api/auth/reset-password
 */
import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, CheckCircle2, AlertTriangle, Loader2, KeyRound,
} from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

/* ============================================================== */
/* Shared shell                                                    */
/* ============================================================== */
function PageShell({ children, testId }) {
  return (
    <div className="relative min-h-screen bg-background text-foreground" data-testid={testId}>
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-[0.2]" />
      <div className="pointer-events-none absolute -left-24 top-24 h-80 w-80 rounded-full bg-brand-blue/10 blur-3xl dark:bg-brand-gold/10" />

      <header className="relative z-10 flex items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={36} />
          <div className="leading-tight">
            <div className="text-base font-bold">
              VITAE-PUBLICA<span className="text-[0.6em] align-top">™</span>
            </div>
            <div className="text-[11px] italic text-muted-foreground">
              Les Finances Publiques sont des Tranches de Vies
            </div>
          </div>
        </Link>
        <ThemeToggle />
      </header>

      <div className="relative z-10 mx-auto flex w-full max-w-md flex-col px-6 pt-6 pb-16 animate-fade-in-up">
        {children}
      </div>
    </div>
  );
}

/* ============================================================== */
/* ForgotPassword                                                  */
/* ============================================================== */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("Si l'email existe, un lien de réinitialisation a été envoyé.");
    } catch (err) {
      // Backend always returns 200 anti-enum, mais on gère un éventuel 422.
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Veuillez saisir un email valide.");
    } finally { setBusy(false); }
  }

  return (
    <PageShell testId="forgot-password-page">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
          <KeyRound className="h-6 w-6 text-amber-700 dark:text-amber-400" />
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Mot de passe oublié ?</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Indiquez votre adresse email — nous vous enverrons un lien sécurisé pour
          créer un nouveau mot de passe.
        </p>
      </div>

      {sent ? (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center shadow-sm dark:border-emerald-900 dark:bg-emerald-950/30"
          data-testid="forgot-success-state"
        >
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-600 dark:text-emerald-400" />
          <h2 className="text-base font-semibold">Vérifiez votre boîte mail</h2>
          <p className="mt-2 text-sm text-emerald-900/80 dark:text-emerald-200/80">
            Si un compte existe pour <b className="font-mono">{email}</b>, un email
            contenant un lien de réinitialisation vient d'être envoyé. Le lien expire dans 60 minutes.
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <Button asChild variant="outline" data-testid="forgot-back-to-login">
              <Link to="/login">Retour à la connexion</Link>
            </Button>
            <button
              type="button"
              onClick={() => { setSent(false); setEmail(""); }}
              className="text-xs text-muted-foreground underline-offset-4 hover:underline"
              data-testid="forgot-try-again"
            >
              Essayer une autre adresse
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          data-testid="forgot-form"
          className="rounded-xl border border-border bg-card p-6 shadow-sm"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="vous@exemple.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  data-testid="forgot-email-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="h-11 w-full text-base font-semibold transition active:scale-[0.98]"
              disabled={busy || !email}
              data-testid="forgot-submit-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              {busy ? "Envoi en cours…" : "Envoyer le lien"}
            </Button>
          </div>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-muted-foreground">
        <Link
          to="/login"
          className="font-semibold text-foreground underline-offset-4 hover:underline"
          data-testid="forgot-login-link"
        >
          ← Retour à la connexion
        </Link>
      </p>
    </PageShell>
  );
}

/* ============================================================== */
/* ResetPassword                                                   */
/* ============================================================== */
function scorePw(pw) {
  if (!pw) return { score: 0, label: "—", color: "bg-gray-200" };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  const classes = (/[a-z]/.test(pw) ? 1 : 0) + (/[A-Z]/.test(pw) ? 1 : 0) +
                  (/\d/.test(pw) ? 1 : 0) + (/[^a-zA-Z0-9]/.test(pw) ? 1 : 0);
  s += Math.min(Math.floor(classes / 2), 2);
  s = Math.min(s, 4);
  const labels = ["Très faible", "Faible", "Correct", "Bon", "Excellent"];
  const colors = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-emerald-500", "bg-emerald-600"];
  return { score: s, label: labels[s], color: colors[s] };
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const strength = useMemo(() => scorePw(pw), [pw]);
  const match = pw.length > 0 && pw === confirm;
  const validToken = token && token.length >= 8;

  useEffect(() => {
    if (!validToken) {
      toast.error("Lien invalide ou expiré. Demandez un nouveau lien.");
    }
  }, [validToken]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!match || strength.score < 3) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Mot de passe mis à jour. Vous pouvez vous reconnecter.");
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la réinitialisation.");
    } finally { setBusy(false); }
  }

  return (
    <PageShell testId="reset-password-page">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/40">
          <Lock className="h-6 w-6 text-red-700 dark:text-red-400" />
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Nouveau mot de passe</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Définissez un mot de passe robuste pour sécuriser votre compte.
        </p>
      </div>

      {!validToken ? (
        <div
          className="rounded-xl border border-red-200 bg-red-50 p-6 text-center shadow-sm dark:border-red-900 dark:bg-red-950/30"
          data-testid="reset-invalid-token"
        >
          <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-red-600 dark:text-red-400" />
          <h2 className="text-base font-semibold">Lien invalide ou expiré</h2>
          <p className="mt-2 text-sm text-red-900/80 dark:text-red-200/80">
            Ce lien n'est pas valide. Il a peut-être déjà été utilisé ou il a expiré (60 min).
          </p>
          <Button asChild variant="outline" className="mt-5">
            <Link to="/forgot-password" data-testid="reset-request-new">Demander un nouveau lien</Link>
          </Button>
        </div>
      ) : done ? (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center shadow-sm dark:border-emerald-900 dark:bg-emerald-950/30"
          data-testid="reset-success-state"
        >
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-600 dark:text-emerald-400" />
          <h2 className="text-base font-semibold">Mot de passe mis à jour</h2>
          <p className="mt-2 text-sm text-emerald-900/80 dark:text-emerald-200/80">
            Redirection vers la connexion…
          </p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          data-testid="reset-form"
          className="rounded-xl border border-border bg-card p-6 shadow-sm"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-pw">Nouveau mot de passe</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="new-pw"
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="pl-9 pr-10"
                  data-testid="reset-new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Afficher/masquer"
                  data-testid="reset-toggle-pw"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {pw.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="reset-password-strength">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map(i => (
                      <div
                        key={i}
                        className={cn(
                          "h-1 flex-1 rounded-full transition-colors duration-300",
                          i < strength.score ? strength.color : "bg-slate-200 dark:bg-slate-800"
                        )}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-medium">{strength.label}</span>
                    <span className="text-muted-foreground">8+ car. · maj. · chiffre · symbole</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="confirm-pw">Confirmer</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="confirm-pw"
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={cn(
                    "pl-9",
                    confirm.length === 0 ? "" : match ? "border-emerald-400" : "border-red-400"
                  )}
                  data-testid="reset-confirm-password"
                  required
                />
              </div>
              {confirm.length > 0 && !match && (
                <p className="mt-1 flex items-center gap-1 text-[11px] text-red-600">
                  <AlertTriangle className="h-3 w-3" /> Les mots de passe ne correspondent pas.
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="h-11 w-full text-base font-semibold transition active:scale-[0.98]"
              disabled={busy || !match || strength.score < 3}
              data-testid="reset-submit-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              {busy ? "Mise à jour…" : "Mettre à jour le mot de passe"}
            </Button>
          </div>
        </form>
      )}
    </PageShell>
  );
}
